import { useApiClient } from '~/api-client';
import { EventDetailsTabResource } from '@mark43/cad-api';
import { useQuery } from '@tanstack/react-query';
import { useIntegratedStreams } from './use-integrated-streams';
import { TAB_QUERY_CACHE_TIME } from '../../event-panel/tabs/constants';

const streamsToSubscribeTo = ['serviceRotationNotifications', 'nudge'] as const;

export function useIntegratedEventDetailsQuery(props: { eventId?: number; enabled?: boolean }) {
    const apiClient = useApiClient();
    const { eventId } = props;
    const eventDetailsQuery = useQuery(
        ['eventData', eventId] as const,
        ({ queryKey }) => {
            return apiClient
                .request<EventDetailsTabResource.GetEventDetailsTabView>({
                    method: 'GET',
                    url: `event_details_tab/${queryKey[1]}/`,
                })
                .then((data) => {
                    return data.data.data;
                });
        },
        {
            cacheTime: TAB_QUERY_CACHE_TIME,
            enabled: false,
        }
    );

    useIntegratedStreams({
        enabled: props.enabled,
        streamNames: streamsToSubscribeTo,
        query: eventDetailsQuery,
        promiseBufferCallback: () => eventDetailsQuery.refetch(),
        onMessage: (message) => {
            if (message.streamName === 'serviceRotationNotifications') {
                if (message.payload.agencyEventId === eventId) {
                    return [eventId];
                }
            } else if (message.streamName === 'nudge') {
                const renNudgeKeys = ['REN']; // read as reporting event number
                const eventNudgeKeys = ['E', 'EC']; // read id as event id
                const eventEventAssociationNudgeKeys = ['EEA']; // for associated events
                const eventEventLinkNudgekeys = ['EEL']; // for linked events

                // TODO: Need to add a new nudge to handle service rotation realtime updates
                for (const key of [
                    ...eventEventAssociationNudgeKeys,
                    ...renNudgeKeys,
                    ...eventEventLinkNudgekeys,
                ]) {
                    const entries = message.payload.map[key];
                    if (!entries) continue;
                    for (const entry of entries) {
                        if (eventId && entry.e === eventId) {
                            return [eventId];
                        }
                    }
                }

                // If the event updates, run the callback
                for (const key of eventNudgeKeys) {
                    const entries = message.payload.map[key];
                    if (!entries) continue;
                    for (const entry of entries) {
                        if (eventId && entry.id === eventId) {
                            return [eventId];
                        }
                    }
                }
            }
            return [];
        },
    });

    return eventDetailsQuery;
}
